// We are not going down the road of typing Asclepeion structures here.
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  KioskLanding,
  KioskRiskSections,
  PatientRiskSections,
  RiskhubItem,
  AdminContentContextType,
  ContentConfiguration,
} from "./types";

function parseContentConfigurations(configs: any): ContentConfiguration[] {
  if (!Array.isArray(configs)) {
    return [];
  }
  return configs.map((config: any) => ({
    query: config.query || "",
    content: config.content || "",
    videoUrl: config.video_url || "",
    videoTitle: config.video_title || "",
    isAiPrompt: config.is_ai_prompt || false,
  }));
}

function getImageUrl(data: any): string {
  const assets = data.assets;

  if (assets.logo.medium.substring(0, 4) === "http") {
    return assets.logo.medium;
  } else {
    return assets.root + assets.logo.medium;
  }
}

function getAccountType(data: any): string {
  if (data?.account_type) {
    return data.account_type;
  }

  return "Standard";
}

function getKioskLandingData(data: any): KioskLanding {
  const landingPage = data.settings.point_of_care.landing_page;

  return {
    pageTitle: landingPage.title,
    disclaimer: landingPage.disclaimer,
    videoUrl: landingPage.video_url,
    videoEnabled: landingPage.video_enabled,
  };
}

function getKioskResultsData(data: any): KioskRiskSections {
  const pointOfCare = data.settings.point_of_care;

  const testing = pointOfCare.testing;
  const breastMri = pointOfCare.tyrer_cuzick;
  const lungCt = pointOfCare.lung_screening;
  const counseling = pointOfCare.counseling;
  const average = pointOfCare.average;

  return {
    testing: {
      headerImageUrl: testing.header_image_url,
      bodyHtml: testing.body_html,
      videoUrl: testing.video_url,
      videoEnabled: testing.video_enabled,
      patientIdentifierEnabled: testing.show_patient_identifier,
      bioEnabled: testing.bio_enabled,
      bioHtml: testing.bio_html,
      bioImageUrl: testing.bio_image_url,
      showScheduler: testing.show_scheduler,
      showPatientHub: testing.show_patient_hub,
      collectAvailability: testing.collect_availability,
      lockPatientHub: testing.lock_patient_hub,
      lockRestart: testing.lock_restart,
      lockScheduler: testing.lock_scheduler,
      lockCode: testing.lock_code,
    },
    breastMri: {
      headerImageUrl: breastMri.header_image_url,
      bodyHtml: breastMri.body_html,
      videoUrl: breastMri.video_url,
      videoEnabled: breastMri.video_enabled,
      patientIdentifierEnabled: breastMri.show_patient_identifier,
      bioEnabled: breastMri.bio_enabled,
      bioHtml: breastMri.bio_html,
      bioImageUrl: breastMri.bio_image_url,
      showScheduler: breastMri.show_scheduler,
      showPatientHub: breastMri.show_patient_hub,
      collectAvailability: breastMri.collect_availability,
      lockPatientHub: breastMri.lock_patient_hub,
      lockRestart: breastMri.lock_restart,
      lockScheduler: breastMri.lock_scheduler,
      lockCode: breastMri.lock_code,
    },
    lungCt: {
      headerImageUrl: lungCt.header_image_url,
      bodyHtml: lungCt.body_html,
      videoUrl: lungCt.video_url,
      videoEnabled: lungCt.video_enabled,
      patientIdentifierEnabled: lungCt.show_patient_identifier,
      bioEnabled: lungCt.bio_enabled,
      bioHtml: lungCt.bio_html,
      bioImageUrl: lungCt.bio_image_url,
      showScheduler: lungCt.show_scheduler,
      showPatientHub: lungCt.show_patient_hub,
      collectAvailability: lungCt.collect_availability,
      lockPatientHub: lungCt.lock_patient_hub,
      lockRestart: lungCt.lock_restart,
      lockScheduler: lungCt.lock_scheduler,
      lockCode: lungCt.lock_code,
    },
    counseling: {
      headerImageUrl: counseling.header_image_url,
      bodyHtml: counseling.body_html,
      videoUrl: counseling.video_url,
      videoEnabled: counseling.video_enabled,
      patientIdentifierEnabled: counseling.show_patient_identifier,
      bioEnabled: counseling.bio_enabled,
      bioHtml: counseling.bio_html,
      bioImageUrl: counseling.bio_image_url,
      showScheduler: counseling.show_scheduler,
      showPatientHub: counseling.show_patient_hub,
      collectAvailability: counseling.collect_availability,
      lockPatientHub: counseling.lock_patient_hub,
      lockRestart: counseling.lock_restart,
      lockScheduler: counseling.lock_scheduler,
      lockCode: counseling.lock_code,
    },
    average: {
      headerImageUrl: average.header_image_url,
      bodyHtml: average.body_html,
      videoUrl: average.video_url,
      videoEnabled: average.video_enabled,
      patientIdentifierEnabled: average.show_patient_identifier,
      bioEnabled: average.bio_enabled,
      bioHtml: average.bio_html,
      bioImageUrl: average.bio_image_url,
      showScheduler: average.show_scheduler,
      showPatientHub: average.show_patient_hub,
      collectAvailability: average.collect_availability,
      lockPatientHub: average.lock_patient_hub,
      lockRestart: average.lock_restart,
      lockScheduler: average.lock_scheduler,
      lockCode: average.lock_code,
    },
  };
}

function getPatientResultsData(data: any): PatientRiskSections {
  const offsite = data.settings.offsite;

  const testing = offsite.testing;
  const breastMri = offsite.tyrer_cuzick;
  const lungCt = offsite.lung_screening;
  const counseling = offsite.counseling;
  const average = offsite.average;

  return {
    testing: {
      enabled: testing.enabled ?? true,
      title: testing.title || "",
      shortText: testing.short_text || "",
      shortTextAvg: testing.short_text_avg || "",
      category: testing.category || "",
      nextStepsHeader: testing.next_steps_header || "",
      nextStepsHeaderAvg: testing.next_steps_header_avg || "",
      nextStepsHeaderContent: testing.next_steps_header_html || "",
      nextStepsHeaderContentAvg: testing.next_steps_header_html_avg || "",
      videoUrl: testing.video_url,
      videoTitle: testing.video_title || "",
      videoEnabled: testing.video_enabled,
      showScheduler: testing.show_scheduler,
      showSchedulerAvg: testing.show_scheduler_avg ?? false,
      interestIndicatorLabel: testing.interest_indicator_label || "",
      callToActionText: testing.call_to_action_text,
      collectAvailability: testing.collect_availability,
      bodyHtml: testing.body_html || "",
      bodyHtmlAvg: testing.body_html_avg || "",
      increasingFactorsTitle: testing.increasing_factors_title || "",
      decreasingFactorsTitle: testing.decreasing_factors_title || "",
      contentConfigurations: parseContentConfigurations(testing.content_configurations),
    },
    breastMri: {
      enabled: breastMri.enabled ?? true,
      title: breastMri.title || "",
      shortText: breastMri.short_text || "",
      shortTextAvg: breastMri.short_text_avg || "",
      category: breastMri.category || "",
      nextStepsHeader: breastMri.next_steps_header || "",
      nextStepsHeaderAvg: breastMri.next_steps_header_avg || "",
      nextStepsHeaderContent: breastMri.next_steps_header_html || "",
      nextStepsHeaderContentAvg: breastMri.next_steps_header_html_avg || "",
      videoUrl: breastMri.video_url,
      videoTitle: breastMri.video_title || "",
      videoEnabled: breastMri.video_enabled,
      showScheduler: breastMri.show_scheduler,
      showSchedulerAvg: breastMri.show_scheduler_avg ?? false,
      interestIndicatorLabel: breastMri.interest_indicator_label || "",
      callToActionText: breastMri.call_to_action_text,
      collectAvailability: breastMri.collect_availability,
      bodyHtml: breastMri.body_html || "",
      bodyHtmlAvg: breastMri.body_html_avg || "",
      increasingFactorsTitle: breastMri.increasing_factors_title || "",
      decreasingFactorsTitle: breastMri.decreasing_factors_title || "",
      contentConfigurations: parseContentConfigurations(breastMri.content_configurations),
    },
    lungCt: {
      enabled: lungCt.enabled ?? true,
      title: lungCt.title || "",
      shortText: lungCt.short_text || "",
      shortTextAvg: lungCt.short_text_avg || "",
      category: lungCt.category || "",
      nextStepsHeader: lungCt.next_steps_header || "",
      nextStepsHeaderAvg: lungCt.next_steps_header_avg || "",
      nextStepsHeaderContent: lungCt.next_steps_header_html || "",
      nextStepsHeaderContentAvg: lungCt.next_steps_header_html_avg || "",
      videoUrl: lungCt.video_url,
      videoTitle: lungCt.video_title || "",
      videoEnabled: lungCt.video_enabled,
      showScheduler: lungCt.show_scheduler,
      showSchedulerAvg: lungCt.show_scheduler_avg ?? false,
      interestIndicatorLabel: lungCt.interest_indicator_label || "",
      callToActionText: lungCt.call_to_action_text,
      collectAvailability: lungCt.collect_availability,
      bodyHtml: lungCt.body_html || "",
      bodyHtmlAvg: lungCt.body_html_avg || "",
      increasingFactorsTitle: lungCt.increasing_factors_title || "",
      decreasingFactorsTitle: lungCt.decreasing_factors_title || "",
      contentConfigurations: parseContentConfigurations(lungCt.content_configurations),
    },
    counseling: {
      enabled: counseling.enabled ?? true,
      title: counseling.title || "",
      shortText: counseling.short_text || "",
      shortTextAvg: counseling.short_text_avg || "",
      category: counseling.category || "",
      nextStepsHeader: counseling.next_steps_header || "",
      nextStepsHeaderAvg: counseling.next_steps_header_avg || "",
      nextStepsHeaderContent: counseling.next_steps_header_html || "",
      nextStepsHeaderContentAvg: counseling.next_steps_header_html_avg || "",
      videoUrl: counseling.video_url,
      videoTitle: counseling.video_title || "",
      videoEnabled: counseling.video_enabled,
      showScheduler: counseling.show_scheduler,
      showSchedulerAvg: counseling.show_scheduler_avg ?? false,
      interestIndicatorLabel: counseling.interest_indicator_label || "",
      callToActionText: counseling.call_to_action_text,
      collectAvailability: counseling.collect_availability,
      bodyHtml: counseling.body_html || "",
      bodyHtmlAvg: counseling.body_html_avg || "",
      increasingFactorsTitle: counseling.increasing_factors_title || "",
      decreasingFactorsTitle: counseling.decreasing_factors_title || "",
      contentConfigurations: parseContentConfigurations(counseling.content_configurations),
    },
    average: {
      enabled: average.enabled ?? true,
      title: average.title || "",
      shortText: average.short_text || "",
      shortTextAvg: average.short_text_avg || "",
      category: average.category || "",
      nextStepsHeader: average.next_steps_header || "",
      nextStepsHeaderAvg: average.next_steps_header_avg || "",
      nextStepsHeaderContent: average.next_steps_header_html || "",
      nextStepsHeaderContentAvg: average.next_steps_header_html_avg || "",
      videoUrl: average.video_url,
      videoTitle: average.video_title || "",
      videoEnabled: average.video_enabled,
      showScheduler: average.show_scheduler,
      showSchedulerAvg: average.show_scheduler_avg ?? false,
      interestIndicatorLabel: average.interest_indicator_label || "",
      callToActionText: average.call_to_action_text,
      collectAvailability: average.collect_availability,
      bodyHtml: average.body_html || "",
      bodyHtmlAvg: average.body_html_avg || "",
      increasingFactorsTitle: average.increasing_factors_title || "",
      decreasingFactorsTitle: average.decreasing_factors_title || "",
      contentConfigurations: parseContentConfigurations(average.content_configurations),
    },
  };
}

function getRiskhubItems(data: any): RiskhubItem[] {
  const raw = data?.settings?.riskhub;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item: any) => {
    const configs = parseContentConfigurations(
      item.content_configurations ?? item.contentConfigurations
    );
    return {
      enabled: item.enabled ?? true,
      title: item.title || "",
      shortText: item.short_text ?? item.shortText ?? "",
      shortTextAvg: item.short_text_avg ?? item.shortTextAvg ?? "",
      category: item.category || "",
      nextStepsHeader: item.next_steps_header ?? item.nextStepsHeader ?? "",
      nextStepsHeaderAvg: item.next_steps_header_avg ?? item.nextStepsHeaderAvg ?? "",
      nextStepsHeaderContent: item.next_steps_header_html ?? item.nextStepsHeaderContent ?? "",
      nextStepsHeaderContentAvg: item.next_steps_header_html_avg ?? item.nextStepsHeaderContentAvg ?? "",
      videoUrl: item.video_url ?? item.videoUrl,
      videoTitle: item.video_title ?? item.videoTitle ?? "",
      videoEnabled: item.video_enabled ?? item.videoEnabled,
      showScheduler: item.show_scheduler ?? item.showScheduler ?? false,
      showSchedulerAvg: item.show_scheduler_avg ?? item.showSchedulerAvg ?? false,
      interestIndicatorLabel: item.interest_indicator_label ?? item.interestIndicatorLabel ?? "",
      callToActionText: item.call_to_action_text ?? item.callToActionText,
      collectAvailability: item.collect_availability ?? item.collectAvailability,
      bodyHtml: item.body_html ?? item.bodyHtml ?? "",
      bodyHtmlAvg: item.body_html_avg ?? item.bodyHtmlAvg ?? "",
      increasingFactorsTitle: item.increasing_factors_title ?? item.increasingFactorsTitle ?? "",
      decreasingFactorsTitle: item.decreasing_factors_title ?? item.decreasingFactorsTitle ?? "",
      contentConfigurations: configs,
      applicablePatients: item.applicable_patients ?? item.applicablePatients ?? "",
      interest: item.interest ?? "",
      icon: item.icon ?? "",
    };
  });
}

type AdminDataFiltered = Omit<
  NonNullable<AdminContentContextType>,
  "isLoading" | "error"
>;

export function getAdminData(data: any): AdminDataFiltered {
  const imageUrl = getImageUrl(data);
  const accountType = getAccountType(data);
  const isInitialSurvey =
    data.initial_screening_survey?.type === "initial_screening_survey";
  const initialSurveyToken =
    data.initial_screening_survey?.institution_survey_token;

  return {
    imageUrl,
    accountType,
    isInitialSurvey,
    initialSurveyToken,
    locationName: data.name,
    systemName: data.system_name,
    organizationId: data.organization_id,
    organizationName: data.organization_name,
    landing: getKioskLandingData(data),
    kioskResults: getKioskResultsData(data),
    patientResults: getPatientResultsData(data),
    riskhubItems: getRiskhubItems(data),
    featureFlags: data.feature_flags || null,
    rescreen: data.settings.rescreen,
    rescreenWithQuestionnaireEnabled:
      data.settings.patient_surveys?.rescreen_with_questionnaire_enabled ??
      false,
    riskhubContactRequestPrompt:
      data.settings?.riskhub_contact_request_prompt || "",
    riskhubContactRequestIcon:
      data.settings?.riskhub_contact_request_icon || "",
  };
}
